import React, { useState } from 'react';
import { useGetTodayOrderStatusDetailsQuery } from 'Services/Admin/DashboardApiSlice';

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderStatus: string;
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ isOpen, onClose, orderStatus }) => {
  const plant = process.env.REACT_APP_PLANT;
  const [searchQuery, setSearchQuery] = useState('');
  
  const { data, isLoading, isError } = useGetTodayOrderStatusDetailsQuery(
    { plant: plant || "", orderStatus },
    { skip: !isOpen || !orderStatus }
  );

  if (!isOpen) return null;

  const orders = Array.isArray(data) ? data : (data?.results || data?.data || data?.orders || []);

  const filteredOrders = orders.filter((order: any) => {
    const query = searchQuery.toLowerCase();
    const orderId = (order?.doNo || order?.orderId || order?.id || '').toLowerCase();
    const customerName = (order?.customerName || order?.customer || '').toLowerCase();
    
    return orderId.includes(query) || customerName.includes(query);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div>
            <h2 className="text-lg font-bold text-[#1B2559] capitalize">
              Today's {orderStatus} Orders
            </h2>
            <p className="text-xs text-gray-500 font-medium mt-0.5">
              Detailed view of all orders marked as {orderStatus} today.
            </p>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <div className="w-8 h-8 border-4 border-blue-100 border-t-blue-500 rounded-full animate-spin"></div>
              <p className="text-sm font-semibold text-gray-400">Loading order details...</p>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center h-48 gap-2">
              <div className="text-red-400 mb-2">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
              </div>
              <p className="text-sm font-bold text-[#1B2559]">Failed to load data</p>
              <p className="text-xs text-gray-500">Please try again later.</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                 <span className="text-2xl opacity-50">📂</span>
              </div>
              <p className="text-sm font-bold text-[#1B2559]">No Orders Found</p>
              <p className="text-xs text-gray-500 mt-1">There are no {orderStatus} orders for today.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              
              {/* Search Bar */}
              <div className="relative w-full md:w-1/2">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg aria-hidden="true" className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                </div>
                <input 
                  type="text" 
                  className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 outline-none transition-all placeholder:text-gray-400" 
                  placeholder="Search by Order ID or Customer..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Table */}
              <div className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-left border-collapse whitespace-nowrap">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100 text-[11px] uppercase tracking-wider font-bold text-gray-500">
                        <th className="py-3 px-4">Order ID</th>
                        <th className="py-3 px-4">Customer</th>
                        <th className="py-3 px-4">Qty</th>
                        <th className="py-3 px-4">Amount</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4">Date</th>
                      </tr>
                    </thead>
                    <tbody className="text-[13px] font-semibold text-[#1B2559]">
                      {filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-gray-500">
                            No orders match your search "{searchQuery}"
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map((order: any, idx: number) => (
                          <tr key={idx} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                            <td className="py-3 px-4 text-blue-600">
                              {order?.doNo || order?.orderId || order?.id || 'N/A'}
                            </td>
                            <td className="py-3 px-4 max-w-[150px] truncate" title={order?.customerName || order?.customer || 'Guest User'}>
                              {order?.customerName || order?.customer || 'Guest User'}
                            </td>
                            <td className="py-3 px-4">
                              {order?.orderedQty || order?.qty || order?.quantity || 1}
                            </td>
                            <td className="py-3 px-4">
                              ₹{Number(order?.totalAmount || order?.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider border ${
                                (order?.orderStatus || orderStatus).toLowerCase() === 'delivered' 
                                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                : (order?.orderStatus || orderStatus).toLowerCase() === 'shipped'
                                ? 'bg-blue-50 text-blue-600 border-blue-100'
                                : 'bg-amber-50 text-amber-600 border-amber-100'
                              }`}>
                                 {order?.orderStatus || orderStatus}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-500">
                              {order?.orderedDate || (order?.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'Unknown')}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
